import { FC, useEffect, useState } from "react";
import { Slide, SlideshowProps } from "react-slideshow-image";
import Icon from "@wff/components/ui/icon";
import useWindowDimensions from "@wff/hooks/useWindowDimensions";
import classes from "./SliderMultiple.module.scss";

export const SLIDES_TO_SHOW = {
  DESKTOP: 6,
  TABLET: 4,
  MOBILE: 2,
};
export const BREAKPOINTS = {
  DESKTOP: 1280,
  TABLET: 800,
  MOBILE: 300,
};

interface ICalcNumOfSlidesToShow {
  width: number;
  desktopSlidesToShow?: number;
  tabletSlidesToShow?: number;
  mobileSlidesToShow?: number;
  desktopBreakpoint?: number;
  tabletBreakpoint?: number;
  mobileBreakpoint?: number;
}

export const calcNumOfSlidesToShow = ({
  width,
  desktopSlidesToShow = SLIDES_TO_SHOW.DESKTOP,
  tabletSlidesToShow = SLIDES_TO_SHOW.TABLET,
  mobileSlidesToShow = SLIDES_TO_SHOW.MOBILE,
  desktopBreakpoint = BREAKPOINTS.DESKTOP,
  tabletBreakpoint = BREAKPOINTS.TABLET,
  mobileBreakpoint = BREAKPOINTS.MOBILE,
}: ICalcNumOfSlidesToShow): number => {
  const isMobile = width >= mobileBreakpoint && width < tabletBreakpoint;
  const isTablet = width >= tabletBreakpoint && width < desktopBreakpoint;
  const isDesktop = width >= desktopBreakpoint;
  return isDesktop
    ? desktopSlidesToShow
    : isTablet
    ? tabletSlidesToShow
    : isMobile
    ? mobileSlidesToShow
    : 1;
};

interface Props extends SlideshowProps {
  items: any[];
  desktopSlidesToShow?: number;
  mobileSlidesToShow?: number;
  tabletSlidesToShow?: number;
  desktopBreakpoint?: number;
  tabletBreakpoint?: number;
  mobileBreakpoint?: number;
  arrows?: boolean;
  infinite?: boolean;
  indicators?: boolean;
  nextArrowProperties?: {
    showNextArrow?: boolean;
    className?: string;
  };
  prevArrowProperties?: {
    showPrevArrow?: boolean;
    className?: string;
  };
  onChange?: (oldIndex: number, newIndex: number) => void;
  duration?: number;
}

export const SliderMultiple: FC<Props> = ({
  items,
  desktopBreakpoint,
  tabletBreakpoint,
  mobileBreakpoint,
  desktopSlidesToShow,
  tabletSlidesToShow,
  mobileSlidesToShow,
  children,
  arrows,
  infinite,
  indicators,
  nextArrowProperties,
  prevArrowProperties,
  duration,
}) => {
  /**
   * @Note this is a typical "mount gate" that ensure the component is mounted
   * before rendering. This technique is typically used in Nextjs applications
   * when certain values are not the same on the server as they are on the client.
   *
   * A typical error might say "Warning: Prop `className` did not match."
   */
  const [isMounted, setIsMounted] = useState<boolean>(false);

  const { width } = useWindowDimensions();

  const [page, setPage] = useState(0);
  const [isPrevArrowDisabled, setIsPrevArrowDisabled] = useState(true);
  const [isNextArrowDisabled, setIsNextArrowDisabled] = useState(false);

  const slidesToShow = calcNumOfSlidesToShow({
    width,
    desktopBreakpoint: desktopBreakpoint,
    tabletBreakpoint: tabletBreakpoint,
    mobileBreakpoint: mobileBreakpoint,
    desktopSlidesToShow: desktopSlidesToShow,
    tabletSlidesToShow: tabletSlidesToShow,
    mobileSlidesToShow: mobileSlidesToShow,
  });

  useEffect(() => {
    setIsMounted(true);
  }, []);

  const onChange = (_oldIndex: number, newIndex: number) => {
    // no change needed if there is only one date item
    // without this check, the necessary useEffect logic would not fire since page is always 0
    if (items?.length !== 1) {
      setPage(Math.abs(newIndex));
    }
  };
  const itemsLength = items?.length ?? 0;

  useEffect(() => {
    if (page === 0) {
      setIsPrevArrowDisabled(true);
      if (itemsLength && itemsLength < slidesToShow) {
        setIsNextArrowDisabled(true);
      } else {
        setIsNextArrowDisabled(false);
      }
    } else {
      setIsPrevArrowDisabled(false);
      if (itemsLength && page + slidesToShow >= itemsLength) {
        setIsNextArrowDisabled(true);
      } else {
        setIsNextArrowDisabled(false);
      }
    }
  }, [page, itemsLength, slidesToShow]); // TODO from Jared: this styling logic does not work properly on window resize.  Needs to be reimplemented

  const onChangeHandler = (oldIndex: number, newIndex: number) => {
    onChange(oldIndex, newIndex);
  };

  const disabledProperties = {
    cursor: "not-allowed",
    opacity: 0.5,
  };

  const hideArrowProperties = {
    display: "none",
  };

  const properties = {
    autoplay: false,
    infinite,
    arrows: arrows && items?.length > slidesToShow,
    indicators,
    transitionDuration: 300,
    duration: duration,
    prevArrow: (
      <i
        style={{
          cursor: "pointer",
          ...(!prevArrowProperties?.showPrevArrow && hideArrowProperties),
          ...(isPrevArrowDisabled && disabledProperties),
        }}
        data-testid="slider-multiple-prev-arrow"
        className={prevArrowProperties?.className}
      >
        <Icon iconName="arrow-left" />
      </i>
    ),
    nextArrow: (
      <i
        style={{
          cursor: "pointer",
          ...(!nextArrowProperties?.showNextArrow && hideArrowProperties),
          ...(isNextArrowDisabled && disabledProperties),
        }}
        className={nextArrowProperties?.className}
        data-testid="slider-multiple-next-arrow"
      >
        <Icon iconName="arrow-right" />
      </i>
    ),
    responsive: [
      {
        breakpoint: desktopBreakpoint,
        settings: {
          slidesToShow: desktopSlidesToShow,
          slidesToScroll: desktopSlidesToShow,
        },
      },
      {
        breakpoint: tabletBreakpoint,
        settings: {
          slidesToShow: tabletSlidesToShow,
          slidesToScroll: tabletSlidesToShow,
        },
      },
      {
        breakpoint: mobileBreakpoint,
        settings: {
          slidesToShow: mobileSlidesToShow,
          slidesToScroll: mobileSlidesToShow,
        },
      },
    ],
  };

  if (!isMounted) {
    return null;
  }

  return (
    <div className={classes["slider-wrapper"]}>
      <Slide {...properties} onChange={onChangeHandler}>
        {children}
      </Slide>
    </div>
  );
};

SliderMultiple.defaultProps = {
  arrows: false,
  infinite: true,
  indicators: true,
  desktopSlidesToShow: SLIDES_TO_SHOW.DESKTOP,
  tabletSlidesToShow: SLIDES_TO_SHOW.TABLET,
  mobileSlidesToShow: SLIDES_TO_SHOW.MOBILE,
  desktopBreakpoint: BREAKPOINTS.DESKTOP,
  tabletBreakpoint: BREAKPOINTS.TABLET,
  mobileBreakpoint: BREAKPOINTS.MOBILE,

  nextArrowProperties: {
    showNextArrow: true,
    className: "",
  },
  prevArrowProperties: {
    showPrevArrow: true,
    className: "",
  },
  duration: 9999999,
};
